import React from "react";
import { Button, Checkbox, HelpBlock } from "react-bootstrap";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { MoneyFormatter } from "../../../shared/helpers/money/moneyFormatter";
import { OrderCreationPriceCalculator } from "../../../shared/helpers/digitalized-orders/orderCreationPriceCalculator";
import { orderCreationSlice } from "../orderCreationSlice";
import { FreeProductSelector } from "../helpers/FreeProductSelector";
import { OrderUnitsSelector } from "../helpers/OrderUnitsSelector";
import { PriceSummary } from "../../../shared/components/digitalized-orders/data-display/PriceSummary";
import {
  allowedFreeOrderUnitsFilter,
  allowedOrderUnitsFilter,
} from "../helpers/OrderUnitsPage";

export const OrderUnits = props => {
  const dispatch = useDispatch();
  const orderUnitsInfo = useSelector(
    s => s.slices.orderCreation.orderUnitsInfo,
  );

  /**
   * Initialize forms
   */
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: orderUnitsInfo,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "orderUnits",
  });
  const fpFieldArray = useFieldArray({
    control,
    name: "freeProducts",
  });

  /**
   * Things to monitor from redux & the form context
   */
  const allOrderUnits = useSelector(s => s.data.orderUnits);
  const selectedUnits = watch("orderUnits");
  /*
  While the feature is called 'freeProducts', it is actually about free order-units.
  A better naming would be discount-in-kind (German: Naturalrabatt). Originally, we thought
  that Startups would end up giving free products as discount-in-kind. In reality, what happened
  is that they need to be able to give complete OrderUnits as discount-in-kind.
  The naming in the code wasn't fully updated to accomodate that, only the user-facing string.
  */
  const selectedFreeProducts = watch("freeProducts");
  const hasFreeProducts = watch("hasFreeProducts");

  // Filters for the options in the dropdowns
  // They were initially being memoized but
  // useMemo was later removed because it wasn't working properly.
  // The itmes of the arrays allowedOrderUnits and allowedFreeOrderUnits 
  // aren't being changed during selection/deselection but each time 
  // a new array is being created as a result of filter and then map methods.
  // This makes memoization worthless in this case.

  const allowedOrderUnits = allowedOrderUnitsFilter(
    allOrderUnits,
    selectedUnits,
  );

  const allowedFreeOrderUnits = allowedFreeOrderUnitsFilter(
    allOrderUnits,
    selectedFreeProducts,
  );

  /**
   * Success handler
   *
   * @param {object} data - the validated ready to be saved data
   */
  const handleNext = data => {
    if (!data.orderUnits || data.orderUnits.length === 0) {
      setError("orderUnits", {
        message: "Bitte wähle mindestens eine Bestelleinheit.",
        type: "minLength",
      });
      return false;
    }

    if (
      data.hasFreeProducts &&
      (!data.freeProducts || data.freeProducts.length === 0)
    ) {
      setError("freeProducts", {
        message: "Bitte wähle mindestens eine Bestelleinheit.",
        type: "minLength",
      });
      return false;
    }

    dispatch(orderCreationSlice.actions.updateOrderUnitsInfo(data));
    props.navigateNextPage();
    return true;
  };

  /**
   * Go to previous page
   */
  const handlePrevious = () => {
    dispatch(
      orderCreationSlice.actions.updateOrderUnitsInfo({
        orderUnits: selectedUnits,
        freeProducts: selectedFreeProducts,
        hasFreeProducts,
      }),
    );
    props.navigatePreviousPage();
  };

  return (
    <form onSubmit={handleSubmit(handleNext)} className="order-units-page">
      <h3 className="main-heading">Was wurde bestellt?</h3>
      {fields?.map((orderUnit, ouIndex) => (
        <Controller
          render={({
            field: { value, onChange, name, onBlur },
            fieldState,
          }) => (
            <OrderUnitsSelector
              units={allowedOrderUnits}
              value={value}
              onChange={onChange}
              name={name}
              onBlur={onBlur}
              removeOrderUnit={() => remove(ouIndex)}
              error={fieldState.error}
            />
          )}
          key={orderUnit.id}
          name={`orderUnits.${ouIndex}`}
          control={control}
          rules={{
            validate: value => {
              if (!value.orderUnit) {
                return "Bitte wähle eine Bestelleinheit";
              }

              if (
                !(
                  value.orderUnitCount &&
                  value.orderUnitCount > 0 &&
                  value.orderUnitCount < 100
                )
              ) {
                return "Bitte wähle eine Anzahl zwischen 1 und 100";
              }

              return true;
            },
          }}
        />
      ))}

      {errors?.orderUnits && <HelpBlock>{errors.orderUnits.message}</HelpBlock>}

      {selectedUnits.length !==
        allowedOrderUnitsFilter(allOrderUnits).length && (
        <Button
          className="btn btn-secondary"
          onClick={() => {
            clearErrors("orderUnits");
            append({
              orderUnit: null,
              orderUnitCount: null,
            });
          }}
          bsStyle="info"
        >
          {selectedUnits.length > 0
            ? "WEITERE BESTELLEINHEIT AUSWÄHLEN"
            : "BESTELLEINHEIT AUSWÄHLEN"}
        </Button>
      )}

      <div className="divider" />

      <Controller
        render={({ field }) => (
          <Checkbox
            inline
            checked={field.value}
            onChange={e => {
              field.onChange(e.target.checked);
              setValue("freeProducts", []);
              clearErrors("freeProducts");
            }}
          >
            Hast Du Naturalrabatt angeboten für diese Bestelleinheit?
          </Checkbox>
        )}
        name="hasFreeProducts"
        control={control}
      />

      {hasFreeProducts && (
        <>
          {fpFieldArray.fields?.map((fpField, fpFieldIndex) => (
            <Controller
              render={({ field, fieldState }) => (
                <FreeProductSelector
                  {...field}
                  products={allowedFreeOrderUnits}
                  removeProduct={() => fpFieldArray.remove(fpFieldIndex)}
                  error={fieldState.error}
                />
              )}
              key={fpField.id}
              name={`freeProducts.${fpFieldIndex}`}
              control={control}
              rules={{
                validate: value => {
                  if (!value?.orderUnit) {
                    return "Bitte wähle eine Bestelleinheit";
                  }

                  if (
                    !(value?.count && value?.count > 0 && value?.count < 100)
                  ) {
                    return "Bitte wähle eine Anzahl zwischen 1 und 100";
                  }

                  return true;
                },
              }}
            />
          ))}

          {errors?.freeProducts && (
            <HelpBlock>{errors.freeProducts.message}</HelpBlock>
          )}

          {selectedFreeProducts.length !==
            allowedFreeOrderUnitsFilter(allOrderUnits).length && (
            <Button
              className="btn btn-secondary"
              onClick={() => {
                clearErrors("freeProducts");
                fpFieldArray.append({
                  orderUnit: null,
                  count: null,
                });
              }}
              bsStyle="info"
            >
              {selectedFreeProducts.length > 0
                ? "Weitere Bestelleinheiten auswählen"
                : "Bestelleinheiten auswählen"}
            </Button>
          )}
        </>
      )}

      <div className="divider" />

      <PriceSummary
        price={MoneyFormatter.fromBigCents(
          OrderCreationPriceCalculator.getOrderPrice(selectedUnits),
        )}
      />
      <div className="buttons-wrapper">
        <Button className="btn btn-secondary" onClick={handlePrevious}>
          Zurück
        </Button>
        <Button className="btn btn-secondary" type="submit">
          Weiter
        </Button>
      </div>
    </form>
  );
};
